import React, { useState } from 'react';
import Grid2 from '@mui/material/Grid2';
import { Button, Card, CardContent, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import DescriptionIcon from '@mui/icons-material/Description';
import TextField from '../../../components/TextField';
import Picklist from '../../../components/Picklist';
import MultiSelectPicklist from '../../../components/MultiSelectPicklist';
import FormSection from '../../../components/FormSection';
import DocumentViewerDialog from '../../../components/DocumentViewerDialog';
import { CaregiverFormData } from '../types';
import { FieldErrors } from '../validation';
import { TB_SYMPTOM_COMMENT_MAP } from '../constants';

interface Props {
  formData: CaregiverFormData;
  fieldErrors: FieldErrors;
  handleChange: (e: { target: { name: string; value: any } }) => void;
  handleCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMultiSelectChange: (e: { target: { name: string; value: string[] } }) => void;
}

interface ViewerState {
  open: boolean;
  fetchUrl: string;
  title: string;
  initialPage?: number;
}

const CLOSED_VIEWER: ViewerState = { open: false, fetchUrl: '', title: '' };

const MedicalStep: React.FC<Props> = ({
  formData,
  fieldErrors,
  handleChange,
  handleMultiSelectChange,
}) => {
  const { t } = useTranslation();
  const [viewer, setViewer] = useState<ViewerState>(CLOSED_VIEWER);

  const selectedTbSymptoms = formData.tbSymptoms ? formData.tbSymptoms.split(';').filter(Boolean) : [];
  const hasSymptoms = selectedTbSymptoms.some((s) => s !== 'None of the above');

  const openTemplate = (templateName: string, title: string, initialPage?: number) =>
    setViewer({
      open: true,
      fetchUrl: `/api/templates/static/${encodeURIComponent(templateName)}?formName=HHA`,
      title,
      initialPage,
    });

  return (
    <>
      <DocumentViewerDialog
        open={viewer.open}
        onClose={() => setViewer(CLOSED_VIEWER)}
        fetchUrl={viewer.fetchUrl}
        title={viewer.title}
        initialPage={viewer.initialPage}
      />

      <FormSection
        title={t('form.influenza', 'Influenza')}
        icon={<VaccinesIcon />}
        action={
          <Button
            variant="contained"
            startIcon={<DescriptionIcon />}
            size="small"
            onClick={() => openTemplate('Application', t('form.influenza', 'Influenza'), 15)}
          >
            {t('form.viewDocument', 'View Document')}
          </Button>
        }
      >
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Picklist
              fieldName="hasReceivedFluVaccineThisSeason"
              value={formData.hasReceivedFluVaccineThisSeason}
              onChange={handleChange}
              label={t('form.hasReceivedFluVaccineThisSeason', 'Have you received the flu vaccine this season?')}
              objectName="caregiver"
              required
              error={!!fieldErrors.hasReceivedFluVaccineThisSeason}
              helperText={fieldErrors.hasReceivedFluVaccineThisSeason}
            />
          </Grid2>
          {formData.hasReceivedFluVaccineThisSeason === 'Yes' && (
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                name="fluVaccineDate"
                label={t('form.fluVaccineDate', 'Vaccination Date')}
                type="date"
                value={formData.fluVaccineDate}
                onChange={handleChange}
                error={!!fieldErrors.fluVaccineDate}
                helperText={fieldErrors.fluVaccineDate}
                InputLabelProps={{ shrink: true }}
              />
            </Grid2>
          )}
          {formData.hasReceivedFluVaccineThisSeason === 'No' && (
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Picklist
                fieldName="fluVaccineDecision"
                value={formData.fluVaccineDecision}
                onChange={handleChange}
                label={t('form.fluVaccineDecision', 'Do you agree to get the flu vaccine for this flu season?')}
                objectName="caregiver"
                required
                error={!!fieldErrors.fluVaccineDecision}
                helperText={fieldErrors.fluVaccineDecision}
              />
            </Grid2>
          )}
          {formData.hasReceivedFluVaccineThisSeason === 'No' &&
            formData.fluVaccineDecision === 'I disagree' && (
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <MultiSelectPicklist
                  fieldName="fluDeclinationReason"
                  value={formData.fluDeclinationReason}
                  onChange={handleMultiSelectChange}
                  label={t('form.fluDeclinationReason', 'Declination Reason')}
                  objectName="caregiver"
                  required
                  error={!!fieldErrors.fluDeclinationReason}
                  helperText={fieldErrors.fluDeclinationReason}
                />
              </Grid2>
            )}
          {formData.fluDeclinationReason?.includes('Other') && (
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                name="fluDeclinationOtherReason"
                label={t('form.fluDeclinationOtherReason', 'Other Reason')}
                value={formData.fluDeclinationOtherReason}
                onChange={handleChange}
                error={!!fieldErrors.fluDeclinationOtherReason}
                helperText={fieldErrors.fluDeclinationOtherReason}
              />
            </Grid2>
          )}
        </Grid2>
      </FormSection>

      <FormSection
        title={t('form.hepatitisB', 'Hepatitis B')}
        icon={<VaccinesIcon />}
        action={
          <Button
            variant="contained"
            startIcon={<DescriptionIcon />}
            size="small"
            onClick={() => openTemplate('Application', t('form.hepatitisB', 'Hepatitis B'), 16)}
          >
            {t('form.viewDocument', 'View Document')}
          </Button>
        }
      >
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Picklist
              fieldName="sysHepBDecision"
              value={formData.sysHepBDecision}
              onChange={handleChange}
              label={t('form.sysHepBDecision', 'Do you accept the Hepatitis B vaccine at this time?')}
              objectName="caregiver"
              required
              error={!!fieldErrors.sysHepBDecision}
              helperText={fieldErrors.sysHepBDecision}
            />
          </Grid2>
          {formData.sysHepBDecision === 'I decline - I am already immune' && (
            <>
              <Grid2 size={12}>
                <Picklist
                  fieldName="hepBImmuneStatus"
                  value={formData.hepBImmuneStatus}
                  onChange={handleChange}
                  label={t('form.hepBImmuneStatus', 'How are you immune to Hepatitis B?')}
                  objectName="caregiver"
                  required
                  error={!!fieldErrors.hepBImmuneStatus}
                  helperText={fieldErrors.hepBImmuneStatus}
                />
              </Grid2>
              {formData.hepBImmuneStatus === 'Vaccinated' && (
                <>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      required
                      name="hepBInjection1"
                      label={t('form.hepBInjection1', 'Hep B Injection 1')}
                      type="date"
                      value={formData.hepBInjection1}
                      onChange={handleChange}
                      error={!!fieldErrors.hepBInjection1}
                      helperText={fieldErrors.hepBInjection1}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      required
                      name="hepBInjection2"
                      label={t('form.hepBInjection2', 'Hep B Injection 2')}
                      type="date"
                      value={formData.hepBInjection2}
                      onChange={handleChange}
                      error={!!fieldErrors.hepBInjection2}
                      helperText={fieldErrors.hepBInjection2}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      required
                      name="hepBInjection3"
                      label={t('form.hepBInjection3', 'Hep B Injection 3')}
                      type="date"
                      value={formData.hepBInjection3}
                      onChange={handleChange}
                      error={!!fieldErrors.hepBInjection3}
                      helperText={fieldErrors.hepBInjection3}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid2>
                </>
              )}
            </>
          )}
        </Grid2>
      </FormSection>

      <FormSection
        title={t('form.tuberculosis', 'Tuberculosis (TB)')}
        icon={<HealthAndSafetyIcon />}
        action={
          <Button
            variant="contained"
            startIcon={<DescriptionIcon />}
            size="small"
            onClick={() => openTemplate('TB Screening', t('form.tuberculosis', 'Tuberculosis (TB)'))}
          >
            {t('form.viewDocument', 'View Document')}
          </Button>
        }
      >
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <MultiSelectPicklist
              fieldName="tbSymptoms"
              value={formData.tbSymptoms}
              onChange={handleMultiSelectChange}
              label={t('form.tbSymptoms', 'Do you currently have any of the following symptoms?')}
              objectName="caregiver"
              required
              error={!!fieldErrors.tbSymptoms}
              helperText={fieldErrors.tbSymptoms}
              exclusiveOptions={['None of the above']}
            />
            {hasSymptoms && (
              <Card variant="outlined" sx={{ mt: 2, mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ mb: 1, textAlign: 'center' }}>
                    {t('form.tbSymptomComments', 'Do you have any comments about the symptoms you have?')}
                  </Typography>
                  <Grid2 container spacing={2}>
                    {selectedTbSymptoms
                      .filter((symptom) => TB_SYMPTOM_COMMENT_MAP[symptom])
                      .map((symptom) => {
                        const commentField = TB_SYMPTOM_COMMENT_MAP[symptom];
                        return (
                          <Grid2 size={{ xs: 12, sm: 6 }} key={commentField}>
                            <TextField
                              fullWidth
                              name={commentField}
                              label={t(`form.${commentField}`, `${symptom} - Comment`)}
                              value={(formData as any)[commentField] || ''}
                              onChange={handleChange}
                            />
                          </Grid2>
                        );
                      })}
                  </Grid2>
                </CardContent>
              </Card>
            )}
          </Grid2>
          <Grid2 size={12}>
            <Picklist
              fieldName="exposedToTb"
              value={formData.exposedToTb}
              onChange={handleChange}
              label={t('form.exposedToTb', 'Have you been exposed to anyone with the above signs or symptoms or who has tuberculosis?')}
              objectName="caregiver"
              required
              error={!!fieldErrors.exposedToTb}
              helperText={fieldErrors.exposedToTb}
            />
          </Grid2>
        </Grid2>
      </FormSection>
    </>
  );
};

export default MedicalStep;
